import React, { useState, useEffect, useMemo } from 'react';
import { ChevronLeft, X, Target, Play } from 'lucide-react';
import { showToast } from '../utils/toast';
import { api } from '../utils/api';
import { LOCAL_EXERCISES } from '../data/exercises';

import BuilderStepOne from './builder/BuilderStepOne';
import BuilderStepTwo from './builder/BuilderStepTwo';
import BuilderStepThree from './builder/BuilderStepThree';
import ExercisePicker from './builder/ExercisePicker';

const isCardioExerciseName = (name = '') => {
    const n = String(name).toLowerCase();
    return (
        n.includes('hardlopen') || n.includes('rennen') || n.includes('run') ||
        n.includes('roeien') || n.includes('row') || n.includes('concept2') ||
        n.includes('airbike') || n.includes('assault') || n.includes('echo bike') ||
        n.includes('skierg') || n.includes('ski erg') || n.includes('loopband') ||
        n.includes('fiets') || n.includes('crosstrainer') || n.includes('elliptical') ||
        n.includes('stairmaster')
    );
};

const computeDisplayCodes = (exercises = []) => {
    const codes = [];
    let currentLetterIdx = 0;

    for (let i = 0; i < exercises.length; i++) {
        const isCurrentSuperset = i > 0 && !!exercises[i]?.isSuperset;
        const isNextSuperset = (i + 1 < exercises.length) && !!exercises[i + 1]?.isSuperset;

        if (!isCurrentSuperset) {
            if (i > 0) currentLetterIdx++;
            const letter = String.fromCharCode(65 + currentLetterIdx);
            codes.push(isNextSuperset ? `${letter}1` : letter);
        } else {
            const baseLetter = String.fromCharCode(65 + currentLetterIdx);
            let chainCount = 2;
            let back = i - 1;
            while (back > 0 && exercises[back]?.isSuperset) {
                chainCount++;
                back--;
            }
            codes.push(`${baseLetter}${chainCount}`);
        }
    }
    return codes;
};

export default function Builder({ user, setView, setupTimerForWod, exerciseDatabase, requestWakeLock, setCurrentWod }) {
    const [step, setStep] = useState(1);
    const [category, setCategory] = useState(null);
    const [mode, setMode] = useState(null);

    const [title, setTitle] = useState('');
    const [wodFormat, setWodFormat] = useState('AMRAP');
    const [timeCap, setTimeCap] = useState('');
    const [exercises, setExercises] = useState([]);

    const [showPicker, setShowPicker] = useState(false);
    const [searchQ, setSearchQ] = useState('');
    const [expandedEx, setExpandedEx] = useState(null);
    const [quickStarts, setQuickStarts] = useState([]);
    const [showMuscleModal, setShowMuscleModal] = useState(false);
    const [selectedMuscles, setSelectedMuscles] = useState(['Full Body']);
    const MUSCLE_GROUPS = ['Full Body', 'Borst', 'Rug', 'Benen', 'Schouders', 'Armen', 'Core'];

    const toggleMuscle = (m) => {
        if (m === 'Full Body') { setSelectedMuscles(['Full Body']); return; }
        setSelectedMuscles(prev => {
            let newArr = prev.filter(x => x !== 'Full Body');
            if (newArr.includes(m)) newArr = newArr.filter(x => x !== m);
            else newArr = [...newArr, m];
            return newArr.length === 0 ? ['Full Body'] : newArr;
        });
    };

    const glassClass = "bg-zinc-900/90 backdrop-blur-xl border border-zinc-700 shadow-2xl rounded-2xl p-6 text-white";

    const unifiedDictionary = useMemo(() => {
        const seen = new Set();
        const list = [];
        const add = (rawName, tip = '', muscleGroup = '') => {
            const clean = String(rawName || '').trim();
            if (!clean) return;
            const key = clean.toLowerCase();
            if (seen.has(key)) return;
            seen.add(key);
            list.push({ name: clean, exercise_name: clean, tip, muscleGroup });
        };

        if (Array.isArray(exerciseDatabase)) {
            exerciseDatabase.forEach(item => {
                add(item.exercise_name || item.name || item.title, item.tip || item.description, item.muscleGroup || item.muscle_group);
            });
        }
        if (Array.isArray(LOCAL_EXERCISES)) {
            LOCAL_EXERCISES.forEach(item => {
                add(item.title || item.name, item.tip || item.desc, item.muscleGroup || '');
            });
        }
        return list.sort((a, b) => a.name.localeCompare(b.name));
    }, [exerciseDatabase]);

    useEffect(() => {
        const fetchQuickStarts = async () => {
            try {
                const token = localStorage.getItem('token');
                if (token) {
                    const data = await api.fetch('/workouts/history/quickstart', {}, token);
                    if (Array.isArray(data)) setQuickStarts(data);
                }
            } catch(e) {}
        };
        fetchQuickStarts();
    }, []);

    const handleCategorySelect = (cat) => {
        setCategory(cat);
        if (cat === 'Fitness') setTitle('Mijn Krachtsessie');
        if (cat === 'CrossFit') setTitle('Mijn Custom WOD');
        if (cat === 'Cardio') setTitle('Mijn Cardio Sessie');
        setStep(2);
    };

    const handleQuickStart = (wod) => {
        setTitle(wod.type);
        setCategory('CrossFit');
        setMode('Manual');

        let guessedCap = '';
        if (wod.description) {
            const match = wod.description.match(/(\d+)\s+Min/i);
            if (match) guessedCap = match[1];
        }
        setTimeCap(guessedCap);

        try {
            let parsedExercises = typeof wod.workout_data === 'string' ? JSON.parse(wod.workout_data) : wod.workout_data;
            if (Array.isArray(parsedExercises)) {
                const mappedEx = parsedExercises.map((ex, i) => ({
                    id: Date.now() + i,
                    name: ex.name || ex.exercise || 'Oefening',
                    sets: ex.sets || 1,
                    reps: ex.reps || 10,
                    weight: ex.weight || '',
                    metric: ex.metric || 'Reps',
                    repMode: 'standard',
                    isCardioBlock: false,
                    isFailure: false,
                    tempo: ex.tempo || '-',
                    rpe: ex.rpe || '-',
                    rest: ex.rest || 0,
                    isWarmup: ex.isWarmup || false,
                    isDropset: ex.isDropset || false,
                    isSuperset: ex.isSuperset || false
                }));
                setExercises(mappedEx);
            }
        } catch(e) {}

        setStep(3);
    };

    const handleModeSelect = (selectedMode) => {
        setMode(selectedMode);
        if (selectedMode === 'AI') {
            if (category === 'Fitness') {
                setShowMuscleModal(true);
            } else {
                generateAITemplate(category);
                setStep(3);
            }
        } else {
            setExercises([]);
            setTimeCap('');
            if (category === 'CrossFit') setWodFormat('AMRAP');
            if (category === 'Cardio') setWodFormat('Cardio');
            setStep(3);
        }
    };

    const generateAITemplate = (cat, requestedTime = null, muscles = ['Full Body']) => {
        let t = requestedTime;
        const defaultRest = user?.rest_between_sets ?? 75;

        if (cat === 'Fitness') {
            if (!t) t = 45;
            setTimeCap(t.toString());
            const muscleFocus = muscles.join(' & ');
            setTitle(`${muscleFocus} Sessie`);

            let pool = unifiedDictionary.filter(e => {
                if (muscles.includes('Full Body')) return true;
                const n = e.name.toLowerCase();
                if (muscles.includes('Borst') && (n.includes('press') || n.includes('fly') || n.includes('push') || n.includes('pec') || n.includes('bench'))) return true;
                if (muscles.includes('Rug') && (n.includes('row') || n.includes('pull') || n.includes('chin') || n.includes('lat') || n.includes('deadlift'))) return true;
                if (muscles.includes('Benen') && (n.includes('squat') || n.includes('lung') || n.includes('leg') || n.includes('calf') || n.includes('step') || n.includes('hip thrust'))) return true;
                if (muscles.includes('Schouders') && (n.includes('overhead') || n.includes('raise') || n.includes('shoulder') || n.includes('delt'))) return true;
                if (muscles.includes('Armen') && (n.includes('curl') || n.includes('exten') || n.includes('tricep') || n.includes('bicep') || n.includes('pushdown') || n.includes('skull'))) return true;
                if (muscles.includes('Core') && (n.includes('plank') || n.includes('crunch') || n.includes('sit') || n.includes('ab '))) return true;
                return false;
            });

            if (pool.length < 4) pool = unifiedDictionary;

            const shuffled = [...pool].sort(() => 0.5 - Math.random());
            const selected = shuffled.slice(0, 5);

            const exList = selected.map((ex, i) => ({
                id: Date.now() + i,
                name: ex.name,
                sets: i === 0 ? 4 : 3,
                reps: i === 0 ? '8' : '10',
                weight: '',
                rpe: '8',
                tempo: '2010',
                rest: defaultRest,
                repMode: 'standard',
                isCardioBlock: false,
                isFailure: false,
                isWarmup: i === 0,
                isDropset: i === 4,
                isSuperset: false,
                metric: 'Reps'
            }));

            setExercises(exList);
            showToast(`Schema Gegenereerd: ${muscleFocus}`, 'success');

        } else if (cat === 'CrossFit') {
            if (!t) t = 15;
            setWodFormat('AMRAP'); setTimeCap(t.toString());
            if (t <= 10) {
                setExercises([
                    { id: Date.now()+1, name: 'Burpees', sets: 1, reps: '21-15-9', weight: '', metric: 'Reps', repMode: 'scheme' },
                    { id: Date.now()+2, name: 'Kettlebell Swings', sets: 1, reps: '21-15-9', weight: '24kg', metric: 'Reps', repMode: 'scheme' }
                ]);
            } else if (t <= 20) {
                setExercises([
                    { id: Date.now()+1, name: 'Pull-ups', sets: 1, reps: '10', weight: '', metric: 'Reps', repMode: 'standard' },
                    { id: Date.now()+2, name: 'Push-ups', sets: 1, reps: '20', weight: '', metric: 'Reps', repMode: 'standard' },
                    { id: Date.now()+3, name: 'Air Squats', sets: 1, reps: '30', weight: '', metric: 'Reps', repMode: 'standard' }
                ]);
            } else {
                setExercises([
                    { id: Date.now()+1, name: 'Assault AirBike', sets: 1, reps: '20', weight: '', metric: 'Cals', isCardioBlock: true, repMode: 'cardio' },
                    { id: Date.now()+2, name: 'Wall Balls', sets: 1, reps: '30', weight: '9kg', metric: 'Reps', repMode: 'standard' },
                    { id: Date.now()+3, name: 'Toes to Bar', sets: 1, reps: '15', weight: '', metric: 'Reps', repMode: 'standard' }
                ]);
            }
            showToast(`WOD geschaald naar ${t} minuten.`, 'success');

        } else {
            if (!t) t = 40;
            setWodFormat('Cardio'); setTimeCap(t.toString());
            setExercises([{ id: Date.now()+1, name: 'Roeien (Concept2)', sets: 1, reps: '5000', weight: '', metric: 'Meters', isCardioBlock: true, repMode: 'cardio' }]);
            showToast(`Cardio tijd aangepast.`, 'success');
        }
    };

    const addExercise = (exName, forceCardio = false) => {
        const cleanExName = String(exName || '').replace(/^cardio:\s*/i, '').trim();
        const isCardio = forceCardio || category === 'Cardio' || isCardioExerciseName(cleanExName);
        const n = cleanExName.toLowerCase();

        let defaultMetric = 'Reps';
        let defaultReps = '10';
        let defaultSets = 3;

        if (isCardio) {
            defaultSets = 1;
            if (n.includes('bike') || n.includes('assault') || n.includes('ski')) {
                defaultMetric = 'Cals';
                defaultReps = '20';
            } else if (n.includes('row') || n.includes('roeien')) {
                defaultMetric = 'Meters';
                defaultReps = '1000';
            } else {
                defaultMetric = 'Min';
                defaultReps = '15';
            }
        }

        const newEx = {
            id: Date.now(),
            name: cleanExName,
            sets: defaultSets,
            reps: defaultReps,
            weight: '',
            rpe: '-',
            tempo: '-',
            rest: user?.rest_between_sets ?? 75,
            metric: defaultMetric,
            repMode: isCardio ? 'cardio' : 'standard',
            isCardioBlock: isCardio,
            isFailure: false,
            isWarmup: false,
            isDropset: false,
            isSuperset: false
        };
        setExercises(prev => [...prev, newEx]);
        setShowPicker(false);
        setSearchQ('');
        setExpandedEx(newEx.id);
    };

    const updateEx = (id, fieldOrObj, val) => {
        setExercises(prev => prev.map(ex => {
            if (ex.id !== id) return ex;
            if (typeof fieldOrObj === 'object' && fieldOrObj !== null) {
                return { ...ex, ...fieldOrObj };
            }
            return { ...ex, [fieldOrObj]: val };
        }));
    };

    const removeEx = (id) => {
        setExercises(prev => prev.filter(ex => ex.id !== id));
    };

    const buildFinalWodObject = () => {
        const codes = computeDisplayCodes(exercises);
        let desc = "";

        if (category === 'Fitness') {
            desc += `Doel: Krachttraining | Geplande Tijd: ${timeCap ? timeCap + ' Min' : 'Vrij'}\n\n`;
            exercises.forEach((ex, i) => {
                const code = codes[i] || String.fromCharCode(65 + i);
                if (ex.isCardioBlock) {
                    const roundsPrefix = ex.sets && parseInt(ex.sets, 10) > 1 ? `${ex.sets}x ` : '';
                    desc += `- ${roundsPrefix}${ex.reps || 15} ${ex.metric || 'Min'} ${ex.name}${ex.weight ? ` (${ex.weight})` : ''}\n`;
                    return;
                }

                let flags = [];
                if (ex.isWarmup) flags.push("🔥 WARM-UP");
                if (ex.isSuperset) flags.push("🔗 SUPERSET");
                if (ex.isDropset) flags.push("📉 + DROPSET");
                if (ex.isFailure || ex.repMode === 'failure') flags.push("🔥 TOT FAILURE");

                const repDisplay = (ex.isFailure || ex.repMode === 'failure')
                    ? `${ex.sets || 3}x Failure`
                    : (ex.repMode === 'scheme' ? `${ex.reps}` : `${ex.sets || 3}x${ex.reps || 10}`);

                desc += `- ${code} - ${repDisplay} ${ex.name}`;
                if (ex.weight) desc += ` (${ex.weight}kg)`;
                if (flags.length > 0) desc += ` [${flags.join(' | ')}]`;
                desc += `\n`;
            });
        } else if (category === 'Cardio') {
            desc += `Doel: Cardio Sessie | Tijd: ${timeCap ? timeCap + ' Min' : 'Geen limiet'}\n\n`;
            exercises.forEach(ex => {
                const roundsPrefix = ex.sets && parseInt(ex.sets, 10) > 1 ? `${ex.sets}x ` : '';
                desc += `- ${roundsPrefix}${ex.reps || 10} ${ex.metric || 'Min'} ${ex.name}`;
                if (ex.weight) desc += ` (${ex.weight})`;
                desc += `\n`;
            });
        } else {
            desc += `${wodFormat} - ${timeCap ? timeCap + ' Min' : 'For Time'}\n\n`;
            exercises.forEach(ex => {
                const repPart = ex.repMode === 'scheme' ? `${ex.reps}` : `${ex.reps || 10}`;
                desc += `- ${repPart} ${ex.metric || 'Reps'} ${ex.name}`;
                if (ex.weight) desc += ` (${ex.weight})`;
                desc += `\n`;
            });
        }

        return {
            id: 'custom_' + Date.now(),
            title: title || 'Custom Workout',
            type: category,
            format: category === 'Fitness' ? 'Krachttraining' : (category === 'Cardio' ? 'Cardio' : wodFormat),
            timeCap: parseInt(timeCap, 10) * 60 || 0,
            description: desc.trim(),
            isFitness: category === 'Fitness',
            isCustomBuilt: true,
            exercises: exercises.map((ex, i) => {
                const code = codes[i] || String.fromCharCode(65 + i);
                const cleanExName = String(ex.name || '').replace(/^cardio:\s*/i, '').trim();
                const dbMatch = unifiedDictionary.find(d => d.name.toLowerCase() === cleanExName.toLowerCase());
                const isCardioItem = !!ex.isCardioBlock;
                const hasUserEnteredWeight = ex.weight !== undefined && ex.weight !== null && String(ex.weight).trim() !== '';
                const cleanWeight = hasUserEnteredWeight ? (parseFloat(String(ex.weight).replace(/[^0-9.,]/g, '').replace(',', '.')) || 0) : 0;

                return {
                    name: cleanExName,
                    cleanName: cleanExName,
                    sets: parseInt(ex.sets, 10) || 1,
                    reps: ex.isFailure ? 'Failure' : (ex.reps || 10),
                    weight: cleanWeight,
                    hasCustomWeight: hasUserEnteredWeight && cleanWeight > 0,
                    metric: ex.metric || 'Reps',
                    repMode: ex.repMode || 'standard',
                    isFailure: !!ex.isFailure,
                    isCardioBlock: isCardioItem,
                    cardioTarget: isCardioItem ? `${ex.sets > 1 ? ex.sets + 'x ' : ''}${ex.reps} ${ex.metric || 'Min'}${ex.weight ? ' @ ' + ex.weight : ''}` : null,
                    tempo: ex.tempo !== '-' ? ex.tempo : null,
                    rpe: ex.rpe !== '-' ? ex.rpe : null,
                    rest: parseInt(ex.rest, 10) || 0,
                    order: code,
                    displayCode: code,
                    description: dbMatch?.tip || "Concentreer je op een gecontroleerde en veilige uitvoering."
                };
            })
        };
    };

    const handleSave = () => {
        if (exercises.length === 0) return showToast("Voeg tenminste 1 oefening toe.", "error");
        const wodObj = buildFinalWodObject();
        const existing = JSON.parse(localStorage.getItem(`user_saved_wods_${user.id}`) || '[]');
        localStorage.setItem(`user_saved_wods_${user.id}`, JSON.stringify([wodObj, ...existing]));
        showToast("Workout opgeslagen in je Profiel!", "success");
        setView('dashboard');
    };

    const handleStart = async () => {
        if (exercises.length === 0) return showToast("Voeg tenminste 1 oefening toe.", "error");
        const wodObj = buildFinalWodObject();
        if (requestWakeLock) await requestWakeLock();
        setCurrentWod(wodObj);
        setupTimerForWod(wodObj);
        setView('focus');
    };

    const filteredDict = unifiedDictionary
        .filter(e => e.name.toLowerCase().includes(searchQ.toLowerCase()))
        .slice(0, 60);

    return (
        <div className="pb-24 animate-fade-in max-w-5xl mx-auto space-y-6">
            <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-2">
                    {step > 1 && (
                        <button onClick={() => setStep(step - 1)} className="text-gray-300 hover:text-white transition-colors flex items-center gap-2 font-bold text-sm bg-zinc-800 px-4 py-2 rounded-xl border border-zinc-700">
                            <ChevronLeft size={16}/> Terug
                        </button>
                    )}
                    <button onClick={() => setView('dashboard')} className="text-red-500 hover:text-white hover:bg-red-600 transition-colors flex items-center gap-2 font-bold text-sm bg-red-500/10 px-4 py-2 rounded-xl border border-red-500/30">
                        <X size={16}/> Annuleren
                    </button>
                </div>
                <h2 className="text-xl sm:text-2xl font-black text-white uppercase tracking-widest text-right">
                    {step === 1 && "Wat gaan we doen?"}
                    {step === 2 && "Kies je Coach"}
                    {step === 3 && title}
                </h2>
            </div>

            {step === 1 && (
                <BuilderStepOne
                    glassClass={glassClass}
                    handleCategorySelect={handleCategorySelect}
                    quickStarts={quickStarts}
                    handleQuickStart={handleQuickStart}
                    setView={setView}
                />
            )}

            {step === 2 && (
                <BuilderStepTwo
                    glassClass={glassClass}
                    handleModeSelect={handleModeSelect}
                />
            )}

            {step === 3 && (
                <BuilderStepThree
                    glassClass={glassClass}
                    title={title}
                    setTitle={setTitle}
                    category={category}
                    timeCap={timeCap}
                    setTimeCap={setTimeCap}
                    mode={mode}
                    generateAITemplate={generateAITemplate}
                    wodFormat={wodFormat}
                    setWodFormat={setWodFormat}
                    exercises={exercises}
                    expandedEx={expandedEx}
                    setExpandedEx={setExpandedEx}
                    updateEx={updateEx}
                    removeEx={removeEx}
                    setShowPicker={setShowPicker}
                    handleSave={handleSave}
                    handleStart={handleStart}
                    computeDisplayCodes={computeDisplayCodes}
                />
            )}

            {showPicker && (
                <ExercisePicker
                    searchQ={searchQ}
                    setSearchQ={setSearchQ}
                    setShowPicker={setShowPicker}
                    filteredDict={filteredDict}
                    addExercise={addExercise}
                />
            )}
            {showMuscleModal && (
                <div className="fixed inset-0 z-[99999] bg-black/80 flex justify-center items-center p-4 backdrop-blur-sm">
                    <div className="bg-zinc-900 p-6 rounded-3xl w-full max-w-md border border-zinc-700 shadow-2xl flex flex-col animate-fade-in">
                        <div className="flex justify-between items-center mb-6">
                            <h3 className="text-xl font-black text-orange-500 flex items-center gap-2"><Target size={24}/> Spiergroepen</h3>
                            <button onClick={() => setShowMuscleModal(false)} className="text-gray-400 hover:text-white bg-zinc-800 p-2 rounded-full transition-colors"><X size={20}/></button>
                        </div>
                        <p className="text-gray-400 text-sm mb-4">Waar wil je de focus op leggen voor deze training?</p>
                        <div className="flex flex-wrap gap-2 mb-8">
                            {MUSCLE_GROUPS.map(m => (
                                <button key={m} onClick={() => toggleMuscle(m)} className={`px-4 py-2 rounded-xl text-sm font-bold border transition-colors ${selectedMuscles.includes(m) ? 'bg-orange-600 text-white border-orange-500' : 'bg-zinc-800 text-gray-300 border-zinc-700 hover:border-orange-500/50'}`}>
                                    {m}
                                </button>
                            ))}
                        </div>
                        <button onClick={() => {
                            setShowMuscleModal(false);
                            generateAITemplate(category, null, selectedMuscles);
                            setStep(3);
                        }} className="w-full py-4 bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-500 hover:to-red-500 text-white font-black rounded-xl transition-all shadow-lg flex items-center justify-center gap-2 tracking-widest uppercase">
                            GENEREER SCHEMA <Play size={20} fill="currentColor" />
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}
